import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import type { FormSchema } from '@kfolks/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/atoms/ui/tabs'
import { requestCenterApi } from '@/features/request-center'
import { FieldPalette } from './FieldPalette'
import { FormCanvas, dispatchDragEnd } from './FormCanvas'
import { FieldPropertiesPanel } from './FieldPropertiesPanel'
import { FormBuilderHeader } from './FormBuilderHeader'
import { FormBuilderStateProvider, LiveValidationInjector, useFormBuilderState } from './FormBuilderState'
import { FormPreview } from './FormPreview'
import { RawJsonEditor } from './RawJsonEditor'
import { SummaryTemplateInput } from './SummaryTemplateInput'
import { VersionHistoryPanel } from '../shared/version-history/VersionHistoryPanel'

/**
 * Form-builder page shell. Tabs in this exact order, per the
 * `request-center-form-builder` capability spec:
 *
 *     Visual  |  Preview  |  History  |  Raw JSON
 *
 * Default tab on load is **Visual**. The **Raw JSON** tab MUST always be
 * reachable — no `disabled` state, no feature flag, no role gating beyond
 * the route's existing `requireRole({ minRole: UserRole.ADMIN })` guard.
 * Per design D12 (and the explicit `request-center-form-builder` spec
 * requirement "Raw JSON cannot be hidden by feature flag or role"), the
 * Raw JSON tab is the recovery path: if the visual canvas crashes on a
 * malformed payload, the admin must still be able to fix the JSON by hand
 * and save. Hiding the tab on validation failure would weaponize the
 * builder against the admin's recovery options.
 *
 * Shared state lives in `FormBuilderStateProvider` (per design D8 — React
 * context, no global store). The state is seeded from the persisted
 * `formSchema` on first load. The Raw JSON tab continues to use its own
 * editor (mutating-on-save via `PUT /requests/types/:id`); the next
 * subtask wires Raw JSON edits back into the shared state so switching
 * tabs preserves work.
 *
 * Phase D wiring already landed:
 *   - 7.1  Tabbed shell
 *   - 7.2  FieldPalette (filterable, enum-driven)
 *   - 7.3  FormCanvas (drop target + sortable cards) — this commit
 *
 * Still pending:
 *   - 7.4  FieldPropertiesPanel
 *   - 7.5  FormPreview (mounts DynamicFormRenderer per ADR-0010)
 *   - 7.7  SummaryTemplateInput
 *   - 9.x  VersionHistoryPanel + diff modal (shared with workflow builder)
 */
export function FormBuilderPage() {
	const { id } = useParams<{ id: string }>()
	const [activeTab, setActiveTab] = useState<TabValue>(DEFAULT_TAB)

	// Seed the visual builder from the persisted formSchema. Piggy-backs
	// on the admin types list the parent admin page already loads, so
	// the cache is usually warm by the time we mount.
	const list = useQuery({
		queryKey: ['request-types', { admin: true }],
		queryFn: () => requestCenterApi.listRequestTypes({ includeInactive: true, limit: 100 }),
	})
	const persistedType = useMemo(() => list.data?.find((rt) => rt.id === id), [list.data, id])
	const persistedSchema = useMemo<FormSchema | undefined>(
		() => (persistedType ? (persistedType.formSchema as FormSchema) : undefined),
		[persistedType],
	)
	const persistedSummary = persistedType?.summaryTemplate ?? null

	// Gate the provider tree on the persisted-types query so
	// `FormBuilderStateProvider` mounts with the seeded `initialSchema` /
	// `initialSummaryTemplate`. The provider only seeds state on first
	// render, so mounting before the query resolves would leave the canvas
	// stuck on `EMPTY_SCHEMA` forever.
	if (list.isLoading) {
		return (
			<div className="space-y-4">
				<div
					role="status"
					aria-busy="true"
					aria-label="Loading request type"
					className="h-10 animate-pulse rounded-md bg-muted"
				/>
				<div aria-busy="true" className="h-64 animate-pulse rounded-md bg-muted" />
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<FormBuilderStateProvider initialSchema={persistedSchema} initialSummaryTemplate={persistedSummary}>
				<LiveValidationInjector />
				<FormBuilderHeader typeId={id} typeName={persistedType?.name} />
				<Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="w-full">
					<TabsList aria-label="Form builder tabs">
						<TabsTrigger value="visual">Visual</TabsTrigger>
						<TabsTrigger value="preview">Preview</TabsTrigger>
						<TabsTrigger value="history">History</TabsTrigger>
						{/* Raw JSON tab — never disabled, never hidden, never feature-flagged. */}
						<TabsTrigger value="raw-json">Raw JSON</TabsTrigger>
					</TabsList>

					<TabsContent value="visual">
						<VisualTab />
					</TabsContent>

					<TabsContent value="preview">
						<FormPreview />
					</TabsContent>

					<TabsContent value="history">{id ? <VersionHistoryPanel requestTypeId={id} /> : null}</TabsContent>

					<TabsContent value="raw-json">
						{/* Recovery path — always reachable, regardless of state on the other tabs.
						    Wired through the shared `FormBuilderState` so JSON edits round-trip
						    with Visual + Preview. */}
						<RawJsonEditor />
					</TabsContent>
				</Tabs>
			</FormBuilderStateProvider>
		</div>
	)
}

type TabValue = 'visual' | 'preview' | 'history' | 'raw-json'
const DEFAULT_TAB: TabValue = 'visual'

// ─── Visual tab — palette + canvas wrapped in a single DndContext ────────────

function VisualTab() {
	const { insertField, reorderField, selectField } = useFormBuilderState()

	// Pointer sensor for mouse / touch; keyboard sensor for accessibility
	// (per NFR5-003 — fields must be reorderable via the keyboard for
	// non-mouse users).
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	return (
		<DndContext
			sensors={sensors}
			onDragEnd={(event) => dispatchDragEnd(event, { insertField, reorderField, selectField })}
		>
			<div className="space-y-4">
				<section className="rounded-md border bg-card p-3">
					<SummaryTemplateInput />
				</section>
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr_300px]">
					<aside className="rounded-md border bg-card p-3">
						<h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Fields</h3>
						<FieldPalette />
					</aside>
					<section className="space-y-2">
						<FormCanvas />
					</section>
					<aside className="rounded-md border bg-card p-3">
						<h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Properties</h3>
						<FieldPropertiesPanel />
					</aside>
				</div>
			</div>
		</DndContext>
	)
}
